import {
    API_NAME,
    API_URL,
    ASSESSMENTS_API_NAME,
    ASSESSMENTS_API_URL,
    LOCAL_API_URL,
} from '#config/runtimeconfig';
import {
    cognitoCredentialsProvider,
    cognitoUserPoolsTokenProvider,
} from '@aws-amplify/auth/cognito';
import { getCustomerToken, getSessionToken } from './authentication/state/Auth.selectors';

import { Amplify } from 'aws-amplify';
import Logger from './common/helpers/Logger';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getId as getCustomerId } from './authentication/state/customer/AuthCustomer.selectors';
import { getStoreRef } from './storeRef';
import { getId as getUserId } from './authentication/state/user/AuthUser.selectors';
import { parseAmplifyConfig } from 'aws-amplify/utils';

/** @typedef {ReturnType<typeof parseAmplifyConfig>['API']} AmplifyApiConfig */
/** @template T @typedef {T[keyof T]} ValueOf */

const isLocalApi = process.env.NODE_ENV === 'development' && /localhost/.test(API_URL);

// As of Amplify v6.15.5 API url MUST be absolute, not relative:
// https://github.com/aws-amplify/amplify-js/pull/14528
const fixApiUrl = url => {
    // Use the current location to build an absolute URL
    try {
        const absoluteUrl = new URL(url, window.location.href).href;
        // Must remove any trailing slash because internally Amplify adds the `path` which defaults to `/`
        return absoluteUrl.replace(/\/$/, '');
    } catch (error) {
        Logger.error(`Error constructing absolute API URL for Amplify from value "${url}":`, error);
        return url;
    }
};

/** @type {ValueOf<AmplifyApiConfig['REST']>} */
const commonApiEndpoint = {
    endpoint: fixApiUrl(API_URL),
    region: process.env.COGNITO_REGION,
};

/** @type {ValueOf<AmplifyApiConfig['REST']>} */
const localApiEndpoint = {
    endpoint: fixApiUrl(LOCAL_API_URL),
    region: process.env.COGNITO_REGION,
};

/** @type {ValueOf<AmplifyApiConfig['REST']>} */
const assessmentsApiEndpoint = {
    endpoint: fixApiUrl(ASSESSMENTS_API_URL),
    region: process.env.COGNITO_REGION,
};

const REST = {
    [API_NAME]: commonApiEndpoint,
    [ASSESSMENTS_API_NAME]: assessmentsApiEndpoint,
};

if (LOCAL_API_URL && LOCAL_API_URL !== API_URL) {
    REST[process.env.LOCAL_API_NAME] = localApiEndpoint;
}

/**
 * @deprecated This feature has not been re-implemented in Amplify v6 yet.
 * https://github.com/aws-amplify/amplify-js/issues/12282
 * @param {string} url
 * @returns {Promise<Window>} Instance of the window that is loading the url
 */
const _urlOpener = url => {
    return new Promise((resolve, reject) => {
        const windowProxy = window.open(url, '_self');
        if (windowProxy) {
            resolve(windowProxy);
        }
        reject();
    });
};

const amplifyConfig = parseAmplifyConfig({
    Auth: {
        Cognito: {
            allowGuestAccess: false,
            // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
            identityPoolId: process.env.COGNITO_IDP_IDENTITY_POOL_ID,
            // REQUIRED - Amazon Cognito Region
            region: process.env.COGNITO_REGION,
            // OPTIONAL - Amazon Cognito User Pool ID
            userPoolId: process.env.COGNITO_IDP_USER_POOL_ID,
            // OPTIONAL - Amazon Cognito Web Client ID
            userPoolClientId: process.env.COGNITO_IDP_APP_CLIENT_ID,
            loginWith: {
                username: true,
                email: true,
                phone: true,
                // Used for Federated Sign-In
                oauth: {
                    providers: [], // All default SSO providers go in here... eg. Google, Facebook
                    domain: process.env.COGNITO_IDP_USER_POOL_DOMAIN,
                    responseType: /** @type {'code'|'token'} */ (
                        /* TODO: Remove this hard-coding after launch so we do not break existing Cognito setup */ 'code'
                        // process.env.COGNITO_IDP_GRANT_FLOW
                    ),
                    scopes: `${process.env.COGNITO_IDP_OAUTH_CLAIMS}`.split(','),
                    redirectSignIn: [process.env.COGNITO_IDP_SIGNIN_URL],
                    redirectSignOut: [process.env.COGNITO_IDP_SIGNOUT_URL],
                },
            },
        },
    },
    API: {
        REST,
    },
});

export const formatAuthContext = context => {
    const baseContext = context || {};
    let {
        appClientId = process.env.COGNITO_IDP_APP_CLIENT_ID,
        cognitoDomainName = process.env.COGNITO_IDP_USER_POOL_DOMAIN,
        identityPoolId = process.env.COGNITO_IDP_IDENTITY_POOL_ID,
        identityProviderName = 'Cognito',
        ssoEnabled = false,
        userPoolId = process.env.COGNITO_IDP_USER_POOL_ID,
    } = baseContext;
    if (ssoEnabled !== true) {
        appClientId = process.env.COGNITO_APP_CLIENT_ID;
        identityPoolId = process.env.COGNITO_IDENTITY_POOL_ID;
        userPoolId = process.env.COGNITO_USER_POOL_ID;
    }
    return {
        appClientId,
        cognitoDomainName,
        identityPoolId,
        identityProviderName,
        ssoEnabled,
        userPoolId,
    };
};

export const amplifyOptions = {
    API: {
        REST: {
            headers: async ({ apiName }) => {
                try {
                    const state = getStoreRef().getState();
                    const token = getCustomerToken(state);
                    let session = getSessionToken(state);
                    const userId = getUserId(state);
                    const customerId = getCustomerId(state);

                    if (!session) {
                        // Try to get the current session from Amplify if it's not in the state
                        const { tokens: { idToken = '' } = {} } = await fetchAuthSession();
                        session = idToken.toString();
                    }
                    const headers = {
                        ['x-osno-auth']: session,
                    };
                    if (token) {
                        if (isLocalApi) {
                            headers['x-osno-uid'] = `${customerId}~${userId}`;
                        } else {
                            headers['x-osno-uid'] = token;
                        }
                    } else {
                        headers['x-osno-uid'] = `~${userId}`;
                    }

                    return headers;
                } catch (error) {
                    Logger.error(`Error fetching auth session for API "${apiName}":`, error);
                    return {};
                }
            },
        },
    },
};

export const updateTokens = async (
    /** @type {import('@aws-amplify/auth/dist/esm/providers/cognito/tokenProvider/types').CognitoAuthTokens} */ tokens
) => {
    if (tokens) {
        // Store the tokens in storage so they persist across sessions
        await cognitoUserPoolsTokenProvider.authTokenStore.storeTokens(tokens);
    } else {
        await cognitoUserPoolsTokenProvider.authTokenStore.clearTokens();
    }
};

export const updateAuth = context => {
    const formattedContext = formatAuthContext(context);
    const { appClientId, identityPoolId, userPoolId, identityProviderName } = formattedContext;
    // Reconfigure Amplify
    const newConfig = parseAmplifyConfig({
        ...amplifyConfig,
        Auth: {
            ...amplifyConfig.Auth,
            Cognito: {
                ...amplifyConfig.Auth.Cognito,
                identityPoolId,
                userPoolId,
                userPoolClientId: appClientId,
                loginWith: {
                    ...amplifyConfig.Auth.Cognito.loginWith,
                    oauth: {
                        ...amplifyConfig.Auth.Cognito.loginWith.oauth,
                        providers: [
                            ...amplifyConfig.Auth.Cognito.loginWith.oauth.providers,
                            identityProviderName,
                        ],
                    },
                },
            },
        },
    });
    cognitoUserPoolsTokenProvider.setAuthConfig(newConfig.Auth);
    Amplify.configure(newConfig, {
        ...amplifyOptions,
        Auth: {
            tokenProvider: cognitoUserPoolsTokenProvider,
            credentialsProvider: cognitoCredentialsProvider,
        },
    });
    return formattedContext;
};

export default amplifyConfig;
